<script lang="ts">
  import {
    Optica,
    type PacienteOptica, type ExamenOptometrico,
    type OrdenTrabajo, type OrdenTrabajoEstado, type ArticuloOptica,
  } from '../lib/api';

  type Sub = 'pacientes' | 'ordenes' | 'inventario';

  let {
    initialSub = 'pacientes' as Sub,
    showSubNav = true,
    title = 'Óptica',
  }: {
    initialSub?: Sub;
    showSubNav?: boolean;
    title?: string;
  } = $props();

  let sub = $state<Sub>('pacientes');
  let error = $state<string | null>(null);
  let info = $state<string | null>(null);

  function fail(e: any) { error = e?.message ?? String(e); info = null; }
  function ok(m: string) { info = m; error = null; }
  function hoy() { return new Date().toISOString().slice(0, 10); }
  function abrirHtml(html: string) {
    const w = window.open('', '_blank');
    if (w) { w.document.open(); w.document.write(html); w.document.close(); }
  }

  // ── Pacientes + examen optométrico ─────────────────────────────────────────
  let pacientes = $state<PacienteOptica[]>([]);
  let busqueda = $state('');
  let seleccion = $state<PacienteOptica | null>(null);
  let examenes = $state<ExamenOptometrico[]>([]);
  let ordenesPac = $state<OrdenTrabajo[]>([]);

  let nuevoPaciente = $state<PacienteOptica>({ nombre: '', telefono: '', sexo: null, ocupacion: '' });

  function examenVacio(): ExamenOptometrico {
    return {
      fecha: hoy(), optometrista: '', motivo: '', usaLentes: false,
      avScOd: '', avScOi: '', avCcOd: '', avCcOi: '',
      odEsfera: null, odCilindro: null, odEje: null, odAdd: null, odAv: '',
      oiEsfera: null, oiCilindro: null, oiEje: null, oiAdd: null, oiAv: '',
      dipLejos: null, dipCerca: null, tipoLente: 'MONOFOCAL',
      diagnostico: '', tratamientos: '', recomendaciones: '',
    };
  }
  let nuevoExamen = $state<ExamenOptometrico>(examenVacio());

  async function cargarPacientes() {
    try { pacientes = await Optica.pacientes(busqueda || undefined); } catch (e) { fail(e); }
  }

  async function crearPaciente(e: SubmitEvent) {
    e.preventDefault();
    if (!nuevoPaciente.nombre.trim()) { error = 'Nombre del paciente requerido.'; return; }
    try {
      await Optica.crearPaciente(nuevoPaciente);
      nuevoPaciente = { nombre: '', telefono: '', sexo: null, ocupacion: '' };
      ok('Paciente registrado.');
      await cargarPacientes();
    } catch (e) { fail(e); }
  }

  async function abrirFicha(p: PacienteOptica) {
    seleccion = p;
    nuevoExamen = examenVacio();
    try {
      [examenes, ordenesPac] = await Promise.all([Optica.examenesDe(p.id!), Optica.ordenesDe(p.id!)]);
    } catch (e) { fail(e); }
  }

  async function guardarExamen(e: SubmitEvent) {
    e.preventDefault();
    if (!seleccion?.id) return;
    try {
      await Optica.crearExamen(seleccion.id, nuevoExamen);
      ok('Examen optométrico guardado.');
      examenes = await Optica.examenesDe(seleccion.id);
      nuevoExamen = examenVacio();
    } catch (e) { fail(e); }
  }

  async function imprimirReceta(id: string) {
    try { abrirHtml(await Optica.receta(id)); } catch (e) { fail(e); }
  }

  // ── Órdenes de taller ──────────────────────────────────────────────────────
  let ordenes = $state<OrdenTrabajo[]>([]);
  let filtroEstado = $state<OrdenTrabajoEstado | ''>('');
  const ESTADOS: OrdenTrabajoEstado[] = ['RECIBIDA', 'EN_LABORATORIO', 'LISTA', 'ENTREGADA', 'CANCELADA'];

  function ordenVacia(): OrdenTrabajo {
    return {
      pacienteId: '', fecha: hoy(), fechaPromesa: null,
      armazonMarca: '', armazonModelo: '', armazonColor: '',
      micaMaterial: 'ORGANICO', micaDiseno: 'MONOFOCAL', tratamientos: '',
      gOdEsfera: null, gOdCilindro: null, gOdEje: null, gOdAdd: null,
      gOiEsfera: null, gOiCilindro: null, gOiEje: null, gOiAdd: null,
      dip: null, laboratorioExterno: '',
      costoArmazon: 0, costoMicas: 0, otros: 0, anticipo: 0, notas: '',
    };
  }
  let nuevaOrden = $state<OrdenTrabajo>(ordenVacia());

  const totalOrden = $derived(
    (Number(nuevaOrden.costoArmazon) || 0) + (Number(nuevaOrden.costoMicas) || 0) + (Number(nuevaOrden.otros) || 0)
  );

  async function cargarOrdenes() {
    try { ordenes = await Optica.ordenes(filtroEstado || undefined); } catch (e) { fail(e); }
  }

  async function crearOrden(e: SubmitEvent) {
    e.preventDefault();
    if (!nuevaOrden.pacienteId) { error = 'Selecciona el paciente.'; return; }
    try {
      await Optica.crearOrden(nuevaOrden);
      nuevaOrden = ordenVacia();
      ok('Orden de trabajo creada.');
      await cargarOrdenes();
    } catch (e) { fail(e); }
  }

  async function cambiarEstado(o: OrdenTrabajo, estado: OrdenTrabajoEstado) {
    try { await Optica.cambiarEstadoOrden(o.id!, estado); await cargarOrdenes(); ok(`Orden #${o.numero} → ${estado}.`); }
    catch (e) { fail(e); }
  }

  async function imprimirOrden(id: string) {
    try { abrirHtml(await Optica.ordenImprimir(id)); } catch (e) { fail(e); }
  }

  function nombrePaciente(id?: string | null) {
    return pacientes.find(p => p.id === id)?.nombre ?? '—';
  }

  // ── Inventario ─────────────────────────────────────────────────────────────
  let articulos = $state<ArticuloOptica[]>([]);
  let filtroCategoria = $state<ArticuloOptica['categoria'] | ''>('');
  const CATEGORIAS: ArticuloOptica['categoria'][] = ['ARMAZON', 'MICA', 'LENTE_CONTACTO', 'ACCESORIO', 'LIQUIDO', 'SERVICIO'];
  let codigoBarras = $state('');
  let nuevoArticulo = $state<ArticuloOptica>({ codigo: '', nombre: '', categoria: 'ARMAZON', precio: 0, stock: 0 });

  async function cargarArticulos() {
    try { articulos = await Optica.articulos(filtroCategoria || undefined); } catch (e) { fail(e); }
  }

  async function crearArticulo(e: SubmitEvent) {
    e.preventDefault();
    if (!nuevoArticulo.codigo.trim() || !nuevoArticulo.nombre.trim()) { error = 'Código y nombre requeridos.'; return; }
    try {
      await Optica.crearArticulo(nuevoArticulo);
      nuevoArticulo = { codigo: '', nombre: '', categoria: 'ARMAZON', precio: 0, stock: 0 };
      ok('Artículo agregado.');
      await cargarArticulos();
    } catch (e) { fail(e); }
  }

  async function buscarBarras(e: SubmitEvent) {
    e.preventDefault();
    if (!codigoBarras.trim()) return;
    try {
      const a = await Optica.articuloPorBarras(codigoBarras.trim());
      ok(`${a.nombre} — Q${Number(a.precio).toFixed(2)} — stock ${a.stock}`);
      codigoBarras = '';
    } catch (e) { error = 'Código de barras no encontrado.'; info = null; }
  }

  async function ajustar(a: ArticuloOptica, delta: number) {
    try { await Optica.ajustarStock(a.id!, delta); await cargarArticulos(); } catch (e) { fail(e); }
  }

  function elegirSub(s: Sub) {
    sub = s; error = null; info = null;
    if (s === 'pacientes') cargarPacientes();
    else if (s === 'ordenes') { cargarOrdenes(); if (pacientes.length === 0) cargarPacientes(); }
    else cargarArticulos();
  }

  $effect(() => {
    sub = initialSub;
    elegirSub(initialSub);
  });
</script>

<div class="opt">
  <div class="ohead">
    <h2>{title}</h2>
    {#if showSubNav}
      <div class="subtabs">
        <button class:active={sub==='pacientes'} onclick={() => elegirSub('pacientes')}>Pacientes y examen</button>
        <button class:active={sub==='ordenes'} onclick={() => elegirSub('ordenes')}>Órdenes de taller</button>
        <button class:active={sub==='inventario'} onclick={() => elegirSub('inventario')}>Inventario</button>
      </div>
    {/if}
  </div>

  {#if error}<p class="msg err">{error}</p>{/if}
  {#if info}<p class="msg ok">{info}</p>{/if}

  {#if sub === 'pacientes'}
    <div class="grid2">
      <section class="card">
        <h3>Nuevo paciente</h3>
        <form onsubmit={crearPaciente} class="form">
          <input placeholder="Nombre completo" bind:value={nuevoPaciente.nombre} />
          <div class="form inline">
            <input placeholder="Teléfono" bind:value={nuevoPaciente.telefono} />
            <input placeholder="Documento (DPI/NIT)" bind:value={nuevoPaciente.documento} />
          </div>
          <div class="form inline">
            <select bind:value={nuevoPaciente.sexo}>
              <option value={null}>— Sexo —</option>
              <option value="MASCULINO">Masculino</option>
              <option value="FEMENINO">Femenino</option>
              <option value="OTRO">Otro</option>
            </select>
            <input placeholder="Ocupación" bind:value={nuevoPaciente.ocupacion} />
          </div>
          <button type="submit">+ Registrar paciente</button>
        </form>
      </section>

      <section class="card">
        <h3>Pacientes</h3>
        <form onsubmit={(e) => { e.preventDefault(); cargarPacientes(); }} class="form inline">
          <input placeholder="Buscar por nombre…" bind:value={busqueda} />
          <button type="submit">Buscar</button>
        </form>
        <table>
          <thead><tr><th>Nombre</th><th>Teléfono</th><th></th></tr></thead>
          <tbody>
            {#each pacientes as p (p.id)}
              <tr class:sel={seleccion?.id===p.id}>
                <td>{p.nombre}</td><td>{p.telefono ?? '—'}</td>
                <td><button class="link" onclick={() => abrirFicha(p)}>Abrir</button></td>
              </tr>
            {/each}
            {#if pacientes.length === 0}<tr><td colspan="3" class="muted">Sin pacientes.</td></tr>{/if}
          </tbody>
        </table>
      </section>
    </div>

    {#if seleccion}
      <section class="card">
        <h3>Examen optométrico — {seleccion.nombre}</h3>
        <form onsubmit={guardarExamen} class="form">
          <div class="form inline">
            <input type="date" bind:value={nuevoExamen.fecha} />
            <input placeholder="Optometrista" bind:value={nuevoExamen.optometrista} />
            <label class="chk"><input type="checkbox" bind:checked={nuevoExamen.usaLentes} /> Usa lentes</label>
            <select bind:value={nuevoExamen.tipoLente}>
              {#each ['MONOFOCAL','BIFOCAL','PROGRESIVO','OCUPACIONAL','CONTACTO'] as t}<option>{t}</option>{/each}
            </select>
          </div>
          <input placeholder="Motivo de consulta" bind:value={nuevoExamen.motivo} />

          <table class="grad">
            <thead><tr><th>Ojo</th><th>Esfera</th><th>Cilindro</th><th>Eje</th><th>Add</th><th>A.V.</th></tr></thead>
            <tbody>
              <tr>
                <td class="eye">OD</td>
                <td><input type="number" step="0.25" bind:value={nuevoExamen.odEsfera} /></td>
                <td><input type="number" step="0.25" bind:value={nuevoExamen.odCilindro} /></td>
                <td><input type="number" step="1" min="0" max="180" bind:value={nuevoExamen.odEje} /></td>
                <td><input type="number" step="0.25" bind:value={nuevoExamen.odAdd} /></td>
                <td><input placeholder="20/20" bind:value={nuevoExamen.odAv} /></td>
              </tr>
              <tr>
                <td class="eye">OI</td>
                <td><input type="number" step="0.25" bind:value={nuevoExamen.oiEsfera} /></td>
                <td><input type="number" step="0.25" bind:value={nuevoExamen.oiCilindro} /></td>
                <td><input type="number" step="1" min="0" max="180" bind:value={nuevoExamen.oiEje} /></td>
                <td><input type="number" step="0.25" bind:value={nuevoExamen.oiAdd} /></td>
                <td><input placeholder="20/20" bind:value={nuevoExamen.oiAv} /></td>
              </tr>
            </tbody>
          </table>

          <div class="form inline">
            <label class="lbl">DIP lejos<input type="number" step="0.5" bind:value={nuevoExamen.dipLejos} /></label>
            <label class="lbl">DIP cerca<input type="number" step="0.5" bind:value={nuevoExamen.dipCerca} /></label>
          </div>
          <input placeholder="Diagnóstico" bind:value={nuevoExamen.diagnostico} />
          <input placeholder="Tratamientos (antirreflejo, filtro azul, fotocromático…)" bind:value={nuevoExamen.tratamientos} />
          <input placeholder="Recomendaciones" bind:value={nuevoExamen.recomendaciones} />
          <button type="submit">Guardar examen</button>
        </form>

        <h3 style="margin-top:1rem;">Historial de exámenes</h3>
        <table>
          <thead><tr><th>Fecha</th><th>OD</th><th>OI</th><th>Tipo</th><th>Dx</th><th></th></tr></thead>
          <tbody>
            {#each examenes as ex (ex.id)}
              <tr>
                <td>{ex.fecha}</td>
                <td>{ex.odEsfera ?? '—'} / {ex.odCilindro ?? '—'} x {ex.odEje ?? '—'}</td>
                <td>{ex.oiEsfera ?? '—'} / {ex.oiCilindro ?? '—'} x {ex.oiEje ?? '—'}</td>
                <td>{ex.tipoLente ?? '—'}</td>
                <td>{ex.diagnostico ?? '—'}</td>
                <td><button class="link" onclick={() => imprimirReceta(ex.id!)}>Receta</button></td>
              </tr>
            {/each}
            {#if examenes.length === 0}<tr><td colspan="6" class="muted">Sin exámenes.</td></tr>{/if}
          </tbody>
        </table>

        {#if ordenesPac.length > 0}
          <h3 style="margin-top:1rem;">Órdenes del paciente</h3>
          <table>
            <thead><tr><th>#</th><th>Fecha</th><th>Estado</th><th>Total</th><th>Saldo</th></tr></thead>
            <tbody>
              {#each ordenesPac as o (o.id)}
                <tr><td>#{o.numero}</td><td>{o.fecha}</td><td><span class="badge">{o.estado}</span></td>
                  <td>Q{Number(o.total).toFixed(2)}</td><td>Q{Number(o.saldo).toFixed(2)}</td></tr>
              {/each}
            </tbody>
          </table>
        {/if}
      </section>
    {/if}

  {:else if sub === 'ordenes'}
    <section class="card">
      <h3>Nueva orden de taller</h3>
      <form onsubmit={crearOrden} class="form">
        <div class="form inline">
          <select bind:value={nuevaOrden.pacienteId}>
            <option value="">— Paciente —</option>
            {#each pacientes as p (p.id)}<option value={p.id}>{p.nombre}</option>{/each}
          </select>
          <label class="lbl">Fecha promesa<input type="date" bind:value={nuevaOrden.fechaPromesa} /></label>
        </div>
        <div class="form inline">
          <input placeholder="Armazón marca" bind:value={nuevaOrden.armazonMarca} />
          <input placeholder="Modelo" bind:value={nuevaOrden.armazonModelo} />
          <input placeholder="Color" bind:value={nuevaOrden.armazonColor} />
        </div>
        <div class="form inline">
          <select bind:value={nuevaOrden.micaMaterial}>
            {#each ['ORGANICO','POLICARBONATO','ALTO_INDICE','CRISTAL'] as m}<option>{m}</option>{/each}
          </select>
          <select bind:value={nuevaOrden.micaDiseno}>
            {#each ['MONOFOCAL','BIFOCAL','PROGRESIVO','OCUPACIONAL'] as d}<option>{d}</option>{/each}
          </select>
          <input placeholder="Tratamientos" bind:value={nuevaOrden.tratamientos} />
        </div>

        <table class="grad">
          <thead><tr><th>Ojo</th><th>Esfera</th><th>Cilindro</th><th>Eje</th><th>Add</th></tr></thead>
          <tbody>
            <tr>
              <td class="eye">OD</td>
              <td><input type="number" step="0.25" bind:value={nuevaOrden.gOdEsfera} /></td>
              <td><input type="number" step="0.25" bind:value={nuevaOrden.gOdCilindro} /></td>
              <td><input type="number" step="1" min="0" max="180" bind:value={nuevaOrden.gOdEje} /></td>
              <td><input type="number" step="0.25" bind:value={nuevaOrden.gOdAdd} /></td>
            </tr>
            <tr>
              <td class="eye">OI</td>
              <td><input type="number" step="0.25" bind:value={nuevaOrden.gOiEsfera} /></td>
              <td><input type="number" step="0.25" bind:value={nuevaOrden.gOiCilindro} /></td>
              <td><input type="number" step="1" min="0" max="180" bind:value={nuevaOrden.gOiEje} /></td>
              <td><input type="number" step="0.25" bind:value={nuevaOrden.gOiAdd} /></td>
            </tr>
          </tbody>
        </table>

        <div class="form inline">
          <label class="lbl">DIP<input type="number" step="0.5" bind:value={nuevaOrden.dip} /></label>
          <input placeholder="Laboratorio externo" bind:value={nuevaOrden.laboratorioExterno} />
        </div>
        <div class="form inline">
          <label class="lbl">Armazón Q<input type="number" step="0.01" bind:value={nuevaOrden.costoArmazon} /></label>
          <label class="lbl">Micas Q<input type="number" step="0.01" bind:value={nuevaOrden.costoMicas} /></label>
          <label class="lbl">Otros Q<input type="number" step="0.01" bind:value={nuevaOrden.otros} /></label>
          <label class="lbl">Anticipo Q<input type="number" step="0.01" bind:value={nuevaOrden.anticipo} /></label>
          <span class="tot">Total: Q{totalOrden.toFixed(2)} · Saldo: Q{(totalOrden - (Number(nuevaOrden.anticipo) || 0)).toFixed(2)}</span>
        </div>
        <input placeholder="Notas" bind:value={nuevaOrden.notas} />
        <button type="submit">Crear orden</button>
      </form>
    </section>

    <section class="card">
      <div class="form inline" style="justify-content:space-between;">
        <h3 style="margin:0;">Órdenes</h3>
        <select bind:value={filtroEstado} onchange={cargarOrdenes}>
          <option value="">Todos los estados</option>
          {#each ESTADOS as e}<option value={e}>{e}</option>{/each}
        </select>
      </div>
      <table>
        <thead><tr><th>#</th><th>Paciente</th><th>Fecha</th><th>Promesa</th><th>Estado</th><th>Total</th><th>Saldo</th><th>Acciones</th></tr></thead>
        <tbody>
          {#each ordenes as o (o.id)}
            <tr>
              <td>#{o.numero}</td>
              <td>{nombrePaciente(o.pacienteId)}</td>
              <td>{o.fecha}</td>
              <td>{o.fechaPromesa ?? '—'}</td>
              <td><span class="badge">{o.estado}</span></td>
              <td>Q{Number(o.total).toFixed(2)}</td>
              <td>Q{Number(o.saldo).toFixed(2)}</td>
              <td class="acciones">
                <select value={o.estado} onchange={(e) => cambiarEstado(o, (e.currentTarget as HTMLSelectElement).value as OrdenTrabajoEstado)}>
                  {#each ESTADOS as e}<option value={e}>{e}</option>{/each}
                </select>
                <button class="link" onclick={() => imprimirOrden(o.id!)}>Imprimir</button>
              </td>
            </tr>
          {/each}
          {#if ordenes.length === 0}<tr><td colspan="8" class="muted">Sin órdenes.</td></tr>{/if}
        </tbody>
      </table>
    </section>

  {:else}
    <section class="card">
      <h3>Lector de código de barras</h3>
      <form onsubmit={buscarBarras} class="form inline">
        <input placeholder="Escanea o escribe el código de barras…" bind:value={codigoBarras} />
        <button type="submit">Buscar</button>
      </form>
    </section>

    <div class="grid2">
      <section class="card">
        <h3>Nuevo artículo</h3>
        <form onsubmit={crearArticulo} class="form">
          <div class="form inline">
            <input placeholder="Código (SKU)" bind:value={nuevoArticulo.codigo} />
            <input placeholder="Código de barras" bind:value={nuevoArticulo.codigoBarras} />
          </div>
          <input placeholder="Nombre" bind:value={nuevoArticulo.nombre} />
          <div class="form inline">
            <select bind:value={nuevoArticulo.categoria}>
              {#each CATEGORIAS as c}<option value={c}>{c}</option>{/each}
            </select>
            <input placeholder="Marca" bind:value={nuevoArticulo.marca} />
            <input placeholder="Modelo" bind:value={nuevoArticulo.modelo} />
          </div>
          <div class="form inline">
            <label class="lbl">Precio Q<input type="number" step="0.01" bind:value={nuevoArticulo.precio} /></label>
            <label class="lbl">Costo Q<input type="number" step="0.01" bind:value={nuevoArticulo.costo} /></label>
            <label class="lbl">Stock<input type="number" bind:value={nuevoArticulo.stock} /></label>
            <label class="lbl">Mín.<input type="number" bind:value={nuevoArticulo.stockMinimo} /></label>
          </div>
          <button type="submit">+ Agregar artículo</button>
        </form>
      </section>

      <section class="card">
        <div class="form inline" style="justify-content:space-between;">
          <h3 style="margin:0;">Inventario</h3>
          <select bind:value={filtroCategoria} onchange={cargarArticulos}>
            <option value="">Todas las categorías</option>
            {#each CATEGORIAS as c}<option value={c}>{c}</option>{/each}
          </select>
        </div>
        <table>
          <thead><tr><th>Código</th><th>Artículo</th><th>Categoría</th><th>Precio</th><th>Stock</th><th></th></tr></thead>
          <tbody>
            {#each articulos as a (a.id)}
              <tr>
                <td>{a.codigo}</td>
                <td>{a.nombre}</td>
                <td>{a.categoria}</td>
                <td>Q{Number(a.precio).toFixed(2)}</td>
                <td class:low={(a.stock ?? 0) <= (a.stockMinimo ?? 0)}>{a.stock}</td>
                <td class="acciones">
                  <button class="link" onclick={() => ajustar(a, 1)}>+1</button>
                  <button class="link" disabled={(a.stock ?? 0) <= 0} onclick={() => ajustar(a, -1)}>-1</button>
                </td>
              </tr>
            {/each}
            {#if articulos.length === 0}<tr><td colspan="6" class="muted">Sin artículos.</td></tr>{/if}
          </tbody>
        </table>
      </section>
    </div>
  {/if}
</div>

<style>
  .opt { display: flex; flex-direction: column; gap: 1rem; }
  .ohead { display: flex; align-items: baseline; gap: 1rem; flex-wrap: wrap; justify-content: space-between; }
  .ohead h2 { margin: 0; color: #166534; font-size: 1.4rem; }
  .subtabs { display: flex; gap: .25rem; flex-wrap: wrap; }
  .subtabs button { padding: .4rem .9rem; border: 1px solid #e5e7eb; background: #fff; border-radius: 999px; cursor: pointer; color: #15803d; font: inherit; }
  .subtabs button.active { background: #16a34a; color: #fff; border-color: #16a34a; }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
  @media (max-width: 820px) { .grid2 { grid-template-columns: 1fr; } }
  .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 1rem; background: #fff; box-shadow: 0 1px 3px rgb(0 0 0 / 5%); }
  .card h3 { margin: 0 0 .6rem; color: #166534; font-size: 1rem; }
  .form { display: flex; flex-direction: column; gap: .5rem; }
  .form.inline { flex-direction: row; flex-wrap: wrap; align-items: center; }
  input, select { padding: .5rem .65rem; border: 1px solid #e5e7eb; border-radius: 8px; font: inherit; background: #fff; color: #111827; }
  input:focus, select:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  .chk { display: flex; align-items: center; gap: .4rem; font-size: .85rem; color: #6b7280; }
  .lbl { display: flex; flex-direction: column; gap: .15rem; font-size: .72rem; color: #6b7280; font-weight: 600; text-transform: uppercase; letter-spacing: .03em; }
  .lbl input { width: 92px; }
  button { font: inherit; }
  .form button[type=submit] { padding: .5rem .9rem; background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; border: 0; border-radius: 8px; cursor: pointer; font-weight: 600; align-self: flex-start; box-shadow: 0 4px 14px rgba(22,163,74,0.3); transition: transform 0.15s; }
  .form button[type=submit]:hover { transform: translateY(-1px); }
  .link:disabled { opacity: .5; cursor: not-allowed; }
  .link { background: none; border: 0; color: #16a34a; cursor: pointer; text-decoration: underline; padding: 0 .3rem; }
  table { width: 100%; border-collapse: collapse; margin-top: .5rem; font-size: .88rem; }
  th { text-align: left; padding: .5rem .8rem; color: #6b7280; font-weight: 600; font-size: .7rem; text-transform: uppercase; letter-spacing: .03em; background: #f0fdf4; }
  td { text-align: left; padding: .4rem .8rem; border-top: 1px solid #f3f4f6; color: #111827; }
  tr.sel { background: #f0fdf4; }
  .muted { color: #6b7280; text-align: center; padding: .8rem; }
  .badge { padding: .1rem .5rem; border-radius: 999px; background: #dcfce7; color: #166534; font-size: .72rem; }
  .acciones { display: flex; gap: .4rem; align-items: center; }
  .tot { font-weight: 600; color: #166534; }
  td.low { color: #dc2626; font-weight: 700; }
  .grad { width: auto; }
  .grad th, .grad td { text-align: center; }
  .grad td.eye { font-weight: 700; background: #f0fdf4; }
  .grad input { width: 84px; }
  .msg { padding: .55rem .8rem; border-radius: 8px; margin: 0; }
  .msg.err { background: #fee2e2; color: #dc2626; }
  .msg.ok { background: #dcfce7; color: #166534; }
</style>
